import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { MdClose, MdSearch } from 'react-icons/md';
import { useDebounce } from '@blaze-react/utils/lib/customHooks';
import useSearchResults from '../useSearchResults';
import { SEARCH_PUBLISHED, DEFAULT_RESULTS_KEYS, SEARCH_BUTTON } from '../../constants';

const SearchPublishedContent = ({
  entities,
  children,
  resultKeys,
  getIsOpenSearchBar,
  SearchIcon,
  CloseIcon,
  className,
  onChange,
  placeholder,
  onEnterPress,
  inputRef,
  isSearchBarOpen,
  fields
}) => {
  const [displaySearchBar, setDisplaySearchBar] = useState(isSearchBarOpen);
  const [searchTerm, setSearchTerm] = useState('');
  const [previousSearchTerm, setPreviousSearchTerm] = useState(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [{ loading, results }, setQueryParams] = useSearchResults({
    queryKey: SEARCH_PUBLISHED,
    resultKeys,
    entities,
    fields
  });

  useEffect(
    () => {
      if (isSearchBarOpen !== displaySearchBar) {
        setDisplaySearchBar(isSearchBarOpen);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSearchBarOpen]
  );

  useEffect(
    () => {
      if (inputRef && inputRef.current && displaySearchBar) {
        inputRef.current.focus();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displaySearchBar]
  );

  useEffect(
    () => {
      if (debouncedSearchTerm && debouncedSearchTerm !== previousSearchTerm) {
        setQueryParams({ value: `${debouncedSearchTerm}*` });
        setPreviousSearchTerm(debouncedSearchTerm);
      }
    },
    [debouncedSearchTerm, previousSearchTerm, setQueryParams]
  );

  const handleOnChange = ({ target: { value = '' } = {} }) => {
    onChange(value);
    setSearchTerm(value);
  };

  const handleKeyPress = event => {
    const onEnter = event.key === 'Enter';
    onEnter && onEnterPress();
  };

  const handleSearchBar = () => {
    const voidSearchTerm = '';
    displaySearchBar && setSearchTerm(voidSearchTerm);
    handleOnChange({ value: voidSearchTerm });
    if (isSearchBarOpen !== null) {
      getIsOpenSearchBar(!displaySearchBar);
    }
    setDisplaySearchBar(!displaySearchBar);
  };

  const searchWrapperClassname = classnames('search__wrapper', {
    'search__wrapper--toggle-display': displaySearchBar,
    'search__wrapper--toggle': !displaySearchBar
  });

  const triggerClassname = classnames('search__icon', {
    open: displaySearchBar
  });

  return (
    <div className={`search-wrapper ${className}`}>
      <div className="search search--header search--toggle">
        <div className={searchWrapperClassname}>
          <input
            data-testid="search-input"
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            type="text"
            onChange={handleOnChange}
            value={searchTerm}
            ref={inputRef}
          />
          <button type="button" className="icon-button icon-button--search icon-button--toggle">
            <SearchIcon />
          </button>
        </div>
        <div
          role="button"
          className={triggerClassname}
          onClick={handleSearchBar}
          id={SEARCH_BUTTON}
          title={SEARCH_BUTTON}>
          {!displaySearchBar ? <SearchIcon /> : <CloseIcon />}
        </div>
      </div>
      <div className="search-modal">
        {children({ results: results.length && searchTerm ? results : [], loading })}
      </div>
    </div>
  );
};

SearchPublishedContent.propTypes = {
  entities: PropTypes.array,
  children: PropTypes.func.isRequired,
  resultKeys: PropTypes.string,
  getIsOpenSearchBar: PropTypes.func,
  SearchIcon: PropTypes.func,
  CloseIcon: PropTypes.func,
  onChange: PropTypes.func,
  onEnterPress: PropTypes.func,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  inputRef: PropTypes.object,
  isSearchBarOpen: PropTypes.bool,
  fields: PropTypes.array
};

SearchPublishedContent.defaultProps = {
  entities: ['published_page'],
  placeholder: '',
  resultKeys: DEFAULT_RESULTS_KEYS,
  onChange: () => {},
  onEnterPress: () => {},
  getIsOpenSearchBar: state => state,
  SearchIcon: () => (
    <i>
      <MdSearch />
    </i>
  ),
  CloseIcon: () => (
    <i>
      <MdClose />
    </i>
  ),
  className: '',
  inputRef: null,
  isSearchBarOpen: null,
  fields: []
};

export default SearchPublishedContent;
